import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TOKEN_STORAGE_KEY, TOKEN_STORAGE_TIME } from "store/api/axiosApi";
import { useAppDispatch } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import { onSetLastRoute, useIsLogin } from "store/slices/commonSlice";
import { getLegacyUpdateBillingCluster } from "store/slices/paymentsSlice";
import { clearSlice } from "utils/HelperFunctions";
import {
  getLegacyUpdateBillingRequestFromSearch,
  getLegacyUpdateBillingRoute,
  hasMatchingLegacyUpdateBillingSession,
  LegacyUpdateBillingResolverResponse,
} from "utils/legacyUpdateBillingRoute";
import { goToInvalid } from "utils/invalidRoute";

const LegacyUpdateBillingRedirect = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isLogin = useIsLogin();
  const userData = useUserData();
  const resolverRequest = useMemo(
    () => getLegacyUpdateBillingRequestFromSearch(location.search),
    [location.search]
  );

  useEffect(() => {
    if (!resolverRequest) {
      goToInvalid(navigate, "link");
      return;
    }

    const controller = new AbortController();

    const resolveLegacyRoute = async () => {
      try {
        const response = (await dispatch(
          getLegacyUpdateBillingCluster({
            ...resolverRequest,
            __abortSignal: controller.signal,
          })
        ).unwrap()) as LegacyUpdateBillingResolverResponse;
        const route = getLegacyUpdateBillingRoute(response);

        if (!route) {
          goToInvalid(navigate, "link");
          return;
        }

        // Store the protected destination first. If the existing token expires
        // during navigation, the login flow can still resume this billing link.
        dispatch(onSetLastRoute(route.postLoginRoute));

        const hasToken = Boolean(localStorage.getItem(TOKEN_STORAGE_KEY));
        const hasMatchingSession = hasMatchingLegacyUpdateBillingSession(
          {
            isLogin,
            hasToken,
            accountId: userData?.data?.accountData?.accountid,
            clusterId: userData?.data?.accountData?.clusterid,
          },
          resolverRequest,
          route
        );

        if (hasMatchingSession) {
          navigate(route.postLoginRoute.pathname, { replace: true });
          return;
        }

        // Do not carry a session for another account or tenant into a billing
        // link. clearSlice deliberately preserves the pending lastRoute.
        if (isLogin && hasToken) {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          localStorage.removeItem(TOKEN_STORAGE_TIME);
          clearSlice(dispatch);
        }

        navigate(route.loginPath, { replace: true });
      } catch (error) {
        if (!controller.signal.aborted) {
          goToInvalid(navigate, "link");
        }
      }
    };

    resolveLegacyRoute();

    return () => controller.abort();
  }, [dispatch, isLogin, navigate, resolverRequest, userData]);

  return (
    <div className="h-[var(--shell-vh)] w-full grid place-items-center">
      <div
        className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent"
        role="status"
        aria-label="Opening billing details"
      />
    </div>
  );
};

export default LegacyUpdateBillingRedirect;
